import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Loader2, Mail, Lock, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const YAS_LOGO = '/yas-logo.png';

const OrgLoginPage = ({ setUser }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const res = await fetch(`${API}/api/org/${slug}`);
        if (res.ok) {
          setOrg(await res.json());
        } else {
          setNotFound(true);
        }
      } catch (e) { setNotFound(true); }
      setLoading(false);
    };
    fetchOrg();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter your email and password'); return; }
    setLoggingIn(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (setUser) setUser(data.user);
        const role = data.user.role;
        if (role === 'youth') navigate('/dashboard');
        else if (role === 'lead_monitor' || role === 'coach') navigate('/coach');
        else if (role === 'program_admin' || role === 'admin') navigate('/admin');
        else if (role === 'parent') navigate('/parent');
        else navigate('/dashboard');
      } else {
        toast.error(data.detail || 'Invalid email or password');
      }
    } catch (e) { toast.error('Connection error. Please try again.'); }
    setLoggingIn(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F3EF' }}>
      <div className="text-center">
        <img src={YAS_LOGO} alt="YAS Connect" className="w-16 h-16 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Organization Not Found</h1>
        <p className="text-sm text-gray-500 mb-4">We couldn't find "{slug}". Check the URL and try again.</p>
        <Button onClick={() => navigate('/')} className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white">Go to YAS Connect Home</Button>
      </div>
    </div>
  );

  const brandColor = org.brand_color || '#E86C3D';

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header with org branding */}
      <div style={{ background: `linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 100%)` }} className="py-10 px-4">
        <div className="max-w-md mx-auto text-center">
          {/* Org logo or default */}
          {org.logo_url ? (
            <img src={org.logo_url} alt={org.name} className="w-20 h-20 mx-auto mb-4 rounded-2xl object-contain bg-white p-2" />
          ) : (
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center text-3xl font-black text-white" style={{ backgroundColor: brandColor }}>
              {org.name?.charAt(0) || 'O'}
            </div>
          )}
          <h1 className="text-2xl font-black text-white mb-1">{org.name}</h1>
          {org.tagline && <p className="text-sm text-white/60">{org.tagline}</p>}
        </div>
      </div>

      {/* Login card */}
      <div className="flex-1 flex items-start justify-center px-4 -mt-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-6 space-y-5">
            <div className="text-center">
              <h2 className="text-lg font-bold text-gray-900">Sign In</h2>
              <p className="text-xs text-gray-400 mt-1">Enter your email and password to access the platform</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-3">
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="Your email address" className="pl-10 h-11 rounded-xl border-gray-200 text-sm"
                  data-testid="org-login-email" autoComplete="email" />
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Password" className="pl-10 pr-10 h-11 rounded-xl border-gray-200 text-sm"
                  data-testid="org-login-password" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button type="submit" disabled={loggingIn}
                className="w-full h-12 rounded-xl text-white font-bold text-sm" style={{ backgroundColor: brandColor }}
                data-testid="org-login-submit">
                {loggingIn ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {loggingIn ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            <div className="text-center">
              <button onClick={() => navigate('/forgot-password')} className="text-xs text-gray-400 hover:text-gray-600">
                Forgot your password?
              </button>
            </div>
          </div>

          {/* Programs under this org */}
          {org.programs?.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-[10px] text-gray-400 text-center font-bold uppercase tracking-wider">Programs</p>
              {org.programs.map(p => (
                <div key={p.id} className="bg-white rounded-xl p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: brandColor }}>
                    {p.program_type?.charAt(0)?.toUpperCase() || 'P'}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900">{p.display_name}</p>
                    {p.description && <p className="text-[9px] text-gray-400 truncate">{p.description.substring(0, 60)}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quick links */}
          <div className="mt-4 flex justify-center gap-4 text-[10px]">
            <button onClick={() => navigate('/parent-about')} className="text-gray-400 hover:text-gray-600">For Parents</button>
            <button onClick={() => navigate('/tutorial')} className="text-gray-400 hover:text-gray-600">How It Works</button>
            <button onClick={() => navigate('/')} className="text-gray-400 hover:text-gray-600">About YAS Connect</button>
          </div>

          {/* Powered by */}
          <div className="mt-6 text-center pb-8">
            <div className="flex items-center justify-center gap-2">
              <span className="text-[9px] text-gray-300">Powered by</span>
              <img src={YAS_LOGO} alt="YAS Connect" className="w-24 h-auto opacity-40" />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrgLoginPage;
